import { formatAsBRLWithSignal, isNumber } from '../number';
import { getSeasonalEventPrefix } from '../seasonalEvents';
import { getMostRelevantWords } from '../string';

import OFFER from 'constants/offer';

/**
 * Limits the page title to avoid Google rewriting them
 * See: https://zyppy.com/seo/title-tags/meta-title-tag-length/
 */
const MAX_PAGE_TITLE_RECOMMENDED = 60;

const formatOfferPriceWithSignalForSEO = (offerPrice, offerPriceType) => {
  if (offerPriceType === OFFER.PRICE_TYPES.FREE) {
    return '';
  }

  return isNumber(offerPrice) ? formatAsBRLWithSignal(offerPrice) : offerPrice;
};

export const toFormattedOfferPrice = (price, priceType) => {
  if (priceType === OFFER.PRICE_TYPES.COUPON) return 'Cupom';
  if (priceType === OFFER.PRICE_TYPES.FREE) return 'de graça!';

  return price;
};

const formatOfferPageTitleAsProductsSelection = (offerTitle) => {
  if (offerTitle.length <= MAX_PAGE_TITLE_RECOMMENDED) {
    return offerTitle;
  }

  return `${offerTitle.slice(0, MAX_PAGE_TITLE_RECOMMENDED)}...`;
};

/**
 * Unfortunately we don't have a way to know for sure that a specific offer
 * is actually a product. So we make a purely empirical validation
 * based on `offerTitle`.
 */
const isTitleNotFromProduct = (offerTitle) =>
  /(seleção|melhores|promoniver|queremos te ouvir|economize|tudo por|desconto|sorteio|exclusivo|curso|cupom|com até|liquidação|limitado)/i.test(
    offerTitle
  );

const formatOfferTitleMostRelevantWordsWithPrice = (
  offerPrice,
  offerTitleMostRelevantWords
) => `Por ${offerPrice}: ${offerTitleMostRelevantWords}`;

const stripLastSpecialCharacter = (title) =>
  title.replace(/[!?.]+$/, '').trim();

/**
 * Recently we have a lot of duplicated titles that negatively affects the SEO
 * of the `OfferPage`. A way that we found to solve this was to create specific
 * formatting rules for each type of offers. And these rules are:
 * - For offers that are communications of moderation or selection of offers, we
 * just limit the title in 60 characters and add a ellipsis in the end;
 * - For offers that have small titles, we put the price to the start and limit
 * this in 60 chars;
 * - For offers that have big titles, we put the price to the start, attempt
 * to join the product name with your main features and limit this in 60 chars.
 */
export const formatOfferPageTitle = ({
  offerPrice,
  offerPriceType,
  offerTitle,
}) => {
  if (isTitleNotFromProduct(offerTitle)) {
    return formatOfferPageTitleAsProductsSelection(offerTitle);
  }

  const formattedTitle = formatOfferTitle(offerTitle);

  if (offerPriceType === OFFER.PRICE_TYPES.COUPON) {
    return `Cupom: ${formattedTitle}`;
  }

  if (offerPriceType === OFFER.PRICE_TYPES.FREE) {
    return `De graça: ${formattedTitle}`;
  }

  const formattedPrice = formatOfferPriceWithSignalForSEO(
    offerPrice,
    offerPriceType
  );

  return formatOfferTitleMostRelevantWordsWithPrice(
    formattedPrice,
    formattedTitle
  );
};

export const formatOfferTitle = (offerTitle) => {
  if (isTitleNotFromProduct(offerTitle)) {
    return formatOfferPageTitleAsProductsSelection(offerTitle);
  }

  const mostRelevantWords = getMostRelevantWords(offerTitle);
  const minimumOfRelevantWordsToTruncate = 4;

  if (mostRelevantWords.length <= minimumOfRelevantWordsToTruncate) {
    return mostRelevantWords.join(' ');
  }

  const offerPartialName = mostRelevantWords.slice(0, 3).join(' ');
  const lastOfferFeatures = mostRelevantWords.slice(-2).join(' ');
  const truncatedOfferTitle = `${offerPartialName} ${lastOfferFeatures}`.slice(
    0,
    MAX_PAGE_TITLE_RECOMMENDED
  );

  return truncatedOfferTitle;
};

const getOfferPrefixFromEvent = () => {
  const seasonalEvent = getSeasonalEventPrefix();

  return seasonalEvent ? `${seasonalEvent}:` : 'Promoção de hoje:';
};

const buildOfferPrefix = (isFinishedOffer, isOfferTitleNotFromProduct) => {
  if (isFinishedOffer) {
    return 'Promoção encerrada: ';
  }

  if (isOfferTitleNotFromProduct) {
    return '';
  }

  return `${getOfferPrefixFromEvent()} `;
};

const buildStoreFragment = ({ hasCoupon, storeDomain, storeName }) => {
  const currStoreName = storeName || storeDomain;
  const cleanStoreName = stripLastSpecialCharacter(currStoreName);

  return hasCoupon
    ? `com cupom de desconto exclusivo na ${cleanStoreName}`
    : `na ${cleanStoreName}`;
};

const buildPriceFragment = (formattedPrice, offerPriceType) =>
  offerPriceType === OFFER.PRICE_TYPES.FREE
    ? 'de graça'
    : `por ${formattedPrice}`;

const buildProductSelectionDescription = ({
  cleanTitle,
  hasCouponInTitle,
  prefix = '',
  storeFragment,
}) =>
  hasCouponInTitle
    ? `${prefix}${cleanTitle} ${storeFragment}.`
    : `${prefix}${cleanTitle}.`;

const buildNormalProductDescription = ({
  cleanTitle,
  offerPriceType,
  prefix,
  priceFragment,
  storeFragment,
}) =>
  offerPriceType === OFFER.PRICE_TYPES.COUPON
    ? `${prefix}${cleanTitle} ${storeFragment}.`
    : `${prefix}${cleanTitle} ${priceFragment} ${storeFragment}.`;

const buildBaseDescription = ({
  cleanTitle,
  hasCouponInTitle,
  isOfferTitleNotFromProduct,
  offerPriceType,
  prefix,
  priceFragment,
  storeFragment,
}) => {
  const isProductSelection = isOfferTitleNotFromProduct;

  if (isProductSelection) {
    return buildProductSelectionDescription({
      prefix,
      cleanTitle,
      storeFragment,
      hasCouponInTitle,
    });
  }

  return buildNormalProductDescription({
    prefix,
    cleanTitle,
    storeFragment,
    offerPriceType,
    priceFragment,
  });
};

const buildExtrasList = (hasCommunityComments, hasPriceHistory) => {
  const extras = [];

  if (hasPriceHistory) extras.push('histórico de preço');
  if (hasCommunityComments) extras.push('comentários da comunidade');

  return extras;
};

const buildFinishedOfferSuffix = (hasCommunityComments, hasPriceHistory) => {
  const finishedExtras = buildExtrasList(hasCommunityComments, hasPriceHistory);

  return finishedExtras.length > 0
    ? `Veja ${finishedExtras.join(', ')} e ofertas com descontos parecidos!`
    : 'Veja ofertas com descontos parecidos!';
};

const buildSuffixWithExtras = (baseSuffix, extras) =>
  `Veja ${extras.join(', ')} e ${baseSuffix.charAt(0).toLowerCase()}${baseSuffix.slice(1)}`;

const buildActiveOfferSuffix = ({ baseSuffix, baseWithoutExtras, extras }) => {
  if (extras.length === 0) {
    return baseSuffix;
  }

  const OPTIMAL_CHARACTER_LIMIT_FOR_SEO_DESCRIPTION = 160;

  const withExtras = buildSuffixWithExtras(baseSuffix, extras);
  const projectedLength = `${baseWithoutExtras} ${withExtras}`.length;

  if (projectedLength <= OPTIMAL_CHARACTER_LIMIT_FOR_SEO_DESCRIPTION) {
    return withExtras;
  }

  const filteredExtras = extras.filter((e) => e !== 'histórico de preço');

  if (filteredExtras.length > 0) {
    const withFilteredExtras = buildSuffixWithExtras(
      baseSuffix,
      filteredExtras
    );
    const newProjectedLength = `${baseWithoutExtras} ${withFilteredExtras}`
      .length;

    if (newProjectedLength <= OPTIMAL_CHARACTER_LIMIT_FOR_SEO_DESCRIPTION) {
      return withFilteredExtras;
    }
  }

  return baseSuffix;
};

export const formatDynamicOfferPageDescription = ({
  hasCommunityComments,
  hasCoupon,
  hasPriceHistory,
  isFinishedOffer,
  offerPrice,
  offerPriceType,
  offerTitle,
  storeDomain,
  storeName,
}) => {
  const isOfferTitleNotFromProduct = isTitleNotFromProduct(offerTitle);
  const prefix = buildOfferPrefix(isFinishedOffer, isOfferTitleNotFromProduct);
  const formattedTitle = formatOfferTitle(offerTitle);
  const formattedPrice = formatOfferPriceWithSignalForSEO(
    offerPrice,
    offerPriceType
  );
  const storeFragment = buildStoreFragment({
    hasCoupon,
    storeName,
    storeDomain,
  });
  const cleanTitle = stripLastSpecialCharacter(formattedTitle);
  const hasCouponInTitle = offerTitle.toLowerCase().includes('cupom');
  const priceFragment = buildPriceFragment(formattedPrice, offerPriceType);

  const baseWithoutExtras = buildBaseDescription({
    prefix,
    cleanTitle,
    isOfferTitleNotFromProduct,
    storeFragment,
    hasCouponInTitle,
    offerPriceType,
    priceFragment,
  });

  const extras = buildExtrasList(hasCommunityComments, hasPriceHistory);

  const suffix = isFinishedOffer
    ? buildFinishedOfferSuffix(hasPriceHistory, hasCommunityComments)
    : buildActiveOfferSuffix({
        extras,
        baseSuffix: 'Ative alerta para novas promoções!',
        baseWithoutExtras,
      });

  return `${baseWithoutExtras} ${suffix}`;
};
